import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chess } from "chess.js";
import HoodEngineBoardWeb from "../components/HoodEngineBoardWeb";
import HoodLevelPicker from "../components/HoodLevelPicker";
import { getFirstPuzzle } from "../integrations/APIClient";
import { getWebHoodVoiceEnabled, speakWebHoodVoice } from "../utils/hoodWebSpeech";

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
const GOLD = "#FFD400";
const BG = "#061A34";
const CARD = "#102b44";
const TEXT = "#F8FAFC";
const MUTED = "#AFC2D8";

const COPY = {
  en: {
    hello: "Hi, I'm Hood. Would you like to learn chess or improve your skills?",
    learn: "I'm new to chess",
    improve: "I want to improve",
    placeholder: "Tell Hood what you want to do…",
    assessIntro: "Great! I'll show you five quick puzzles so I can understand how you play.",
    bestMove: "Find the best move.",
    almost: "Almost. Try another move — I’m learning how you think.",
    nice: "Nice move! That's the right move. Well done.",
    recovery: "Good recovery. I’m learning how you solve problems.",
    profileTitle: "YOUR FIRST HOOD PROFILE",
    profileIntro: "Great! I start understanding how you play. Let’s see what we can improve together.",
    accuracy: "Move accuracy",
    vision: "Tactical vision",
    persistence: "Persistence",
    focus: "First focus",
    start: "Start my personalised training",
    retry: "Try loading the puzzle again",
    loading: "Hood is preparing your puzzle…",
  },
  pl: {
    hello: "Cześć, jestem Hood. Chcesz nauczyć się szachów od początku czy poprawić swoją grę?",
    learn: "Uczę się od początku",
    improve: "Chcę grać lepiej",
    placeholder: "Powiedz Hoodowi, czego chcesz…",
    assessIntro: "Świetnie! Pokażę Ci pięć szybkich zadań, żebym zobaczył, jak grasz.",
    bestMove: "Znajdź najlepszy ruch.",
    almost: "Prawie. Spróbuj innego ruchu — uczę się, jak myślisz.",
    nice: "Dobry ruch! To jest właściwe rozwiązanie.",
    recovery: "Dobra poprawka. Uczę się, jak rozwiązujesz problemy.",
    profileTitle: "TWÓJ PIERWSZY PROFIL HOODA",
    profileIntro: "Świetnie! Zaczynam rozumieć, jak grasz. Zobaczmy, co możemy poprawić razem.",
    accuracy: "Dokładność ruchów",
    vision: "Widzenie taktyczne",
    persistence: "Wytrwałość",
    focus: "Pierwszy kierunek",
    start: "Zacznij mój spersonalizowany trening",
    retry: "Spróbuj ponownie załadować zadanie",
    loading: "Hood przygotowuje zadanie…",
  },
  es: {
    hello: "Hola, soy Hood. ¿Quieres aprender ajedrez desde cero o mejorar tu nivel?",
    learn: "Quiero aprender desde cero",
    improve: "Quiero mejorar",
    placeholder: "Dile a Hood qué quieres hacer…",
    assessIntro: "¡Genial! Te enseñaré cinco ejercicios rápidos para entender cómo juegas.",
    bestMove: "Encuentra la mejor jugada.",
    almost: "Casi. Prueba otra jugada; estoy aprendiendo cómo piensas.",
    nice: "¡Buena jugada! Esa es la solución correcta.",
    recovery: "Buena recuperación. Estoy aprendiendo cómo resuelves problemas.",
    profileTitle: "TU PRIMER PERFIL DE HOOD",
    profileIntro: "¡Genial! Empiezo a entender cómo juegas. Veamos qué podemos mejorar juntos.",
    accuracy: "Precisión",
    vision: "Visión táctica",
    persistence: "Persistencia",
    focus: "Primer objetivo",
    start: "Empezar mi entrenamiento personalizado",
    retry: "Intentar cargar el ejercicio otra vez",
    loading: "Hood está preparando tu ejercicio…",
  },
};

function textFor(lang) {
  return COPY[lang] || COPY.en;
}

function unwrapPuzzle(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload[0] || null;
  if (payload?.fenPosition || payload?.fen || payload?.after_pgn || payload?.afterPgn) return payload;
  for (const key of ["puzzle", "data", "result", "item"]) {
    const value = payload?.[key];
    if (value?.fenPosition || value?.fen || value?.after_pgn || value?.afterPgn) return value;
  }
  return null;
}

function normalizeSanLine(rawMoves, fen = START_FEN) {
  const tokens = Array.isArray(rawMoves)
    ? rawMoves.map((move) => String(move || "").trim()).filter(Boolean)
    : String(rawMoves || "").trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];
  try {
    const game = new Chess(fen || START_FEN);
    const san = [];
    for (const token of tokens) {
      let move = null;
      try { move = game.move(token, { sloppy: true }); } catch {}
      if (!move && /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(token)) {
        try { move = game.move({ from: token.slice(0, 2), to: token.slice(2, 4), promotion: token[4] || "q" }); } catch {}
      }
      if (!move) break;
      san.push(move.san);
    }
    return san;
  } catch {
    return tokens;
  }
}

function puzzleUserColor(fen) {
  return String(fen || "").trim().split(/\s+/)[1] === "w" ? "b" : "w";
}

function puzzleId(puzzle) {
  return String(puzzle?.id || puzzle?._id?.$oid || puzzle?._id || "");
}

function categoriesOf(puzzle) {
  const rows = Array.isArray(puzzle?.categories) ? puzzle.categories : [];
  return rows.map((value) => String(value || "").trim()).filter(Boolean);
}

function scorePercent(value) {
  return Math.max(12, Math.min(100, Math.round(value)));
}

function EmptyBoard() {
  return <div style={{
    width: "min(100vw,620px)",
    aspectRatio: "1/1",
    display: "grid",
    gridTemplateColumns: "repeat(8,1fr)",
    gridTemplateRows: "repeat(8,1fr)",
    overflow: "hidden",
  }}>
    {Array.from({ length: 64 }, (_, index) => {
      const row = Math.floor(index / 8);
      const col = index % 8;
      return <div key={index} style={{ background: (row + col) % 2 ? "#bca98c" : "#f0d9b5" }} />;
    })}
  </div>;
}

export default function HoodFirstTimeOnboarding({
  lang = "en",
  userId = "guest",
  variantLabel = "ALPHA",
  onComplete,
  ownerMode = false,
  ownerAccountAgeDays = 0,
  onLevelChange,
  onAccountAgeChange,
}) {
  const t = textFor(lang);
  const boardRef = useRef(null);
  const completeLockRef = useRef(false);
  const puzzleMistakesRef = useRef(0);
  const [phase, setPhase] = useState("intro");
  const [speech, setSpeech] = useState(t.hello);
  const [question, setQuestion] = useState("");
  const [boardReady, setBoardReady] = useState(false);
  const [puzzle, setPuzzle] = useState(null);
  const [puzzleNumber, setPuzzleNumber] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [voiceOn] = useState(() => getWebHoodVoiceEnabled());

  const announce = useCallback((value) => {
    const clean = String(value || "").trim();
    if (!clean) return;
    setSpeech(clean);
    if (voiceOn) speakWebHoodVoice(clean, lang);
  }, [lang, voiceOn]);

  useEffect(() => {
    announce(t.hello);
  }, []); // first impression should speak immediately

  const beginLearn = useCallback(() => {
    onComplete?.({ completed: true, path: "learn", completedAt: new Date().toISOString() });
  }, [onComplete]);

  const loadPuzzle = useCallback(async (index, requestedLevel = selectedLevel || 1200) => {
    if (!boardReady) return;
    setLoading(true);
    setLoadError("");
    completeLockRef.current = false;
    puzzleMistakesRef.current = 0;
    try {
      let parsed = null;
      for (let attempt = 0; attempt < 4 && !parsed; attempt += 1) {
        const raw = await getFirstPuzzle(lang, {
          onboarding: 1,
          assessment: index + 1,
          targetElo: requestedLevel,
          rating: requestedLevel,
          nonce: Date.now() + attempt,
        });
        const candidate = unwrapPuzzle(raw);
        const fen = String(candidate?.fenPosition || candidate?.fen || "").trim();
        const rawMoves = candidate?.after_pgn ?? candidate?.afterPgn ?? candidate?.moves;
        const moves = normalizeSanLine(rawMoves, fen);
        if (fen && moves.length >= 2) parsed = { raw: candidate, fen, moves };
      }
      if (!parsed) throw new Error("No assessment puzzle is available.");
      setPuzzle(parsed);
      setPuzzleNumber(index);
      boardRef.current?.setElo?.(requestedLevel);
      announce(index === 0 ? t.assessIntro : t.bestMove);
      boardRef.current?.startPractice?.(parsed.moves, puzzleUserColor(parsed.fen), parsed.fen);
    } catch (error) {
      setLoadError(error?.message || "Could not load the puzzle.");
    } finally {
      setLoading(false);
    }
  }, [announce, boardReady, lang, selectedLevel, t.assessIntro, t.bestMove]);

  const beginImprove = useCallback((requestedLevel = selectedLevel || 1200) => {
    const level = Math.max(800, Number(requestedLevel) || 1200);
    setSelectedLevel(level);
    setPhase("assessment");
    setPuzzleNumber(0);
    setResults([]);
    announce(t.assessIntro);
    boardRef.current?.setElo?.(level);
    if (boardReady) loadPuzzle(0, level);
  }, [announce, boardReady, loadPuzzle, selectedLevel, t.assessIntro]);

  useEffect(() => {
    if (phase === "assessment" && boardReady && !puzzle && !loading && !loadError) loadPuzzle(0, selectedLevel || 1200);
  }, [boardReady, loadError, loadPuzzle, loading, phase, puzzle, selectedLevel]);

  const chooseFromText = useCallback((event) => {
    event.preventDefault();
    const value = question.trim().toLowerCase();
    if (!value) return;
    setQuestion("");
    if (/learn|begin|new|start|od początku|nauc|począt|aprender|principiante|desde cero/.test(value)) beginLearn();
    else beginImprove();
  }, [beginImprove, beginLearn, question]);

  const handleWrong = useCallback(() => {
    puzzleMistakesRef.current += 1;
    boardRef.current?.resolveWrongCandidate?.(false);
    announce(t.almost);
  }, [announce, t.almost]);

  const finishAssessment = useCallback((row) => {
    const nextResults = [...results, row];
    setResults(nextResults);
    const clean = row.mistakes === 0;
    announce(clean ? t.nice : t.recovery);
    if (puzzleNumber >= 4) {
      setTimeout(() => {
        setPhase("profile");
        announce(t.profileIntro);
      }, 850);
      return;
    }
    const nextIndex = puzzleNumber + 1;
    setTimeout(() => {
      setPuzzle(null);
      loadPuzzle(nextIndex, selectedLevel || 1200);
    }, 850);
  }, [announce, loadPuzzle, puzzleNumber, results, selectedLevel, t.nice, t.profileIntro, t.recovery]);

  const handleGuide = useCallback((msg) => {
    if (!msg?.done) return;
    if (completeLockRef.current || !puzzle) return;
    completeLockRef.current = true;
    finishAssessment({
      id: puzzleId(puzzle.raw),
      clean: puzzleMistakesRef.current === 0,
      mistakes: puzzleMistakesRef.current,
      categories: categoriesOf(puzzle.raw),
    });
  }, [finishAssessment, puzzle]);

  const profile = useMemo(() => {
    const clean = results.filter((row) => row.clean).length;
    const mistakes = results.reduce((sum, row) => sum + Number(row.mistakes || 0), 0);
    const themes = [...new Set(results.flatMap((row) => row.categories || []))];
    const accuracy = scorePercent((clean / Math.max(1, results.length)) * 100);
    const tactical = scorePercent(42 + clean * 11 - Math.min(18, mistakes * 3));
    const persistence = scorePercent(results.length ? 82 + Math.min(18, mistakes * 4) : 70);
    return {
      clean,
      mistakes,
      themes,
      accuracy,
      tactical,
      persistence,
      focus: themes[0] || (lang === "pl" ? "szanse taktyczne" : lang === "es" ? "oportunidades tácticas" : "tactical opportunities"),
    };
  }, [lang, results]);

  const finishProfile = useCallback(() => {
    onComplete?.({
      completed: true,
      path: "improve",
      completedAt: new Date().toISOString(),
      profile,
      selectedElo: selectedLevel || 1200,
    });
  }, [onComplete, profile, selectedLevel]);

  return <main style={{ minHeight: "100vh", background: BG, color: TEXT, fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
    <div style={{ maxWidth: 620, margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "max(12px,env(safe-area-inset-top)) 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong style={{ letterSpacing: ".08em" }}>HOOD <span style={{ color: GOLD }}>AI</span></strong>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <HoodLevelPicker
            lang={lang}
            currentElo={selectedLevel}
            compact
            ownerQa={ownerMode}
            accountAgeDays={ownerAccountAgeDays}
            onAccountAgeChange={onAccountAgeChange}
            onSelect={(elo) => {
              setSelectedLevel(elo);
              onLevelChange?.(elo);
              if (elo === 0) beginLearn();
              else if (phase === "intro") beginImprove(elo);
              else boardRef.current?.setElo?.(elo);
            }}
          />
          <span style={{ color: "#7dd3fc", fontSize: 11, fontWeight: 900, letterSpacing: ".1em" }}>{variantLabel} · FIRST SESSION</span>
        </div>
      </div>

      <div style={{ padding: "4px 18px 10px", display: "flex", justifyContent: "center", gap: 8 }}>
        {Array.from({ length: 5 }, (_, index) => <span key={index} style={{
          width: 12, height: 12, borderRadius: 999,
          background: index < results.length ? GOLD : "rgba(255,255,255,.22)",
          border: index === results.length && phase === "assessment" ? "2px solid #fff" : "2px solid transparent",
          transition: "all .2s ease",
        }} />)}
      </div>

      <section style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px 12px" }}>
        <img src="/hood.png" alt="Hood" style={{ width: 86, height: 105, objectFit: "contain", flex: "0 0 auto" }} />
        <div
          role="button"
          tabIndex={0}
          onClick={() => voiceOn && speakWebHoodVoice(speech, lang)}
          onKeyDown={(event) => {
            if (!voiceOn) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              speakWebHoodVoice(speech, lang);
            }
          }}
          style={{ flex: 1, background: "#f7f3e8", color: "#163047", borderRadius: 20, padding: "13px 15px", lineHeight: 1.42, minHeight: 82 }}
        >
          <div style={{ color: "#2474a4", fontSize: 10, fontWeight: 900, letterSpacing: ".09em", marginBottom: 5 }}>HOOD</div>
          {loading ? t.loading : speech}
        </div>
      </section>

      {phase === "intro" && <>
        <div style={{ width: "100%", overflow: "hidden", display: "flex", justifyContent: "center" }}><EmptyBoard /></div>
        <div style={{ padding: "14px 16px 8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          <button onClick={beginLearn} style={secondaryButton}>{t.learn}</button>
          <button onClick={beginImprove} style={primaryButton}>{t.improve}</button>
        </div>
        <form onSubmit={chooseFromText} style={{ padding: "0 16px 18px", display: "flex", gap: 8 }}>
          <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t.placeholder} style={inputStyle} />
          <button type="submit" style={{ ...primaryButton, minWidth: 52, padding: "0 12px" }}>↑</button>
        </form>
      </>}

      {phase === "assessment" && <>
        <div style={{ width: "100vw", maxWidth: 620, alignSelf: "center", overflow: "hidden" }}>
          <HoodEngineBoardWeb
            ref={boardRef}
            boardSize={620}
            onReady={() => setBoardReady(true)}
            onPracticeGuide={handleGuide}
            onPracticeWrongCandidate={handleWrong}
            onEngineError={(message) => setLoadError(String(message || "Board error"))}
          />
        </div>
        <div style={{ padding: "10px 16px 18px", textAlign: "center" }}>
          <div style={{ color: MUTED, fontSize: 11, fontWeight: 900, letterSpacing: ".08em" }}>PUZZLE {Math.min(5, puzzleNumber + 1)} / 5</div>
          <div style={{ marginTop: 4, fontSize: 18, fontWeight: 850 }}>{t.bestMove}</div>
          {loadError && <button onClick={() => loadPuzzle(puzzleNumber)} style={{ ...secondaryButton, marginTop: 10 }}>{t.retry}</button>}
        </div>
      </>}

      {phase === "profile" && <section style={{ padding: "10px 16px 24px" }}>
        <div style={{ background: CARD, border: "1px solid rgba(125,211,252,.28)", borderRadius: 22, padding: 18 }}>
          <div style={{ color: "#7dd3fc", fontSize: 11, fontWeight: 900, letterSpacing: ".1em" }}>{t.profileTitle}</div>
          <h2 style={{ margin: "7px 0 5px", fontSize: 23 }}>{t.profileIntro}</h2>
          <div style={{ display: "grid", gap: 13, marginTop: 18 }}>
            <ProfileBar label={t.accuracy} value={profile.accuracy} />
            <ProfileBar label={t.vision} value={profile.tactical} />
            <ProfileBar label={t.persistence} value={profile.persistence} />
          </div>
          <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,.10)", paddingTop: 13 }}>
            <div style={{ color: MUTED, fontSize: 11, fontWeight: 900 }}>{t.focus.toUpperCase()}</div>
            <div style={{ marginTop: 4, fontWeight: 850 }}>{profile.focus}</div>
          </div>
          <button onClick={finishProfile} style={{ ...primaryButton, width: "100%", marginTop: 18, minHeight: 48 }}>{t.start}</button>
        </div>
      </section>}
    </div>
  </main>;
}

function ProfileBar({ label, value }) {
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, fontWeight: 800 }}>
      <span>{label}</span><span>{value}%</span>
    </div>
    <div style={{ height: 8, borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,.12)", marginTop: 6 }}>
      <div style={{ width: `${value}%`, height: "100%", background: GOLD, borderRadius: 999 }} />
    </div>
  </div>;
}

const primaryButton = {
  border: 0,
  borderRadius: 14,
  background: GOLD,
  color: "#102b40",
  minHeight: 46,
  padding: "10px 13px",
  fontWeight: 900,
  fontSize: 15,
  cursor: "pointer",
};

const secondaryButton = {
  border: "1px solid #49657c",
  borderRadius: 14,
  background: "#19354c",
  color: TEXT,
  minHeight: 46,
  padding: "10px 13px",
  fontWeight: 800,
  fontSize: 15,
  cursor: "pointer",
};

const inputStyle = {
  flex: 1,
  minWidth: 0,
  minHeight: 46,
  border: "1px solid #49657c",
  borderRadius: 14,
  background: "#19354c",
  color: TEXT,
  padding: "10px 12px",
  fontSize: 16,
  outline: "none",
};
